import { editTimesheetRows, timesheetsForWeek, updateTimesheet, allUsers, listLeaveRequests, callFunction } from '../db.js';
import { escapeHtml, pill, initials, titleCase, skeleton, toastV4 } from '../ui.js';
import { openDialog } from './dialog.js';
import { hasFeature } from '../features.js';
import { bulkState, approveEach, approveButtonLabel } from '../time-approvals.js';
import { rangeLabel } from './timesheet.js';
import { openOverlay } from './overlay.js';
import { expectedMinutes } from '../contract-hours.js';
import { weekTeam } from '../timesheet-required.js';
import { orgSetting } from '../org.js';
import { leaveWord } from '../leave.js';

// Timesheet approvals — Coordinators / Finance / Directors / the Owner review
// each person's submitted week: total hours, per-project breakdown, approve or
// reject (with a note the person sees on their sheet).
//
// Appearance: v4.0.0 (design export 22/10/2026, p11; docs/ui-refresh/
// 01-home-workspace.md §7) — a week bar, a figures strip and ONE table, a row
// per person with the day columns, status and the verbs. Every write that
// changes a sheet's state asks first (two presses). The review sheet overlay
// (openReviewDetail) is unchanged. Every value is escaped (house rule 0).
//
// The header verbs (Karan 25/10/2026, spec §7.5; time-approvals.js):
//   Approve submitted — every submitted sheet of the week, after a confirm that
//     lists them, ONE BY ONE through the per-row Approve's own write
//     (updateTimesheet, rules-guarded): a failure is reported, the rest go on.
//   Remind — the remindTimesheets callable (track@): the server recomputes who
//     has no sheet; the confirm shows them and the exact email. Until the
//     server's TIME_REMIND_LIVE is on, the reminder comes to YOU, not to them.

const STATUS_TONES = { draft: '', submitted: 'info', approved: 'ok', rejected: 'error' };
// Same rule as the timesheet: a week locks the following Tuesday 10am (Karan 23/07).
const lockDeadline = (monday) => { const d = new Date(monday); d.setDate(d.getDate() + 8); d.setHours(10, 0, 0, 0); return d; };
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function mondayOf(d) {
  const m = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  m.setDate(m.getDate() - ((m.getDay() + 6) % 7));
  return m;
}
const parse = (v) => {
  if (!v) return 0;
  const s = String(v).trim();
  if (s.includes(':')) { const [h, m] = s.split(':'); return (parseInt(h || 0, 10) * 60) + parseInt(m || 0, 10); }
  const f = parseFloat(s); return isNaN(f) ? 0 : Math.round(f * 60);
};
const hm = (min) => Math.floor(min / 60) + ':' + String(min % 60).padStart(2, '0');
const pad2 = (n) => String(n).padStart(2, '0');
const sheetMinutes = (s) => (s.rows || []).reduce((t, r) =>
  t + Object.values(r.days || {}).reduce((x, v) => x + parse(v), 0), 0);

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export async function renderTimesheetReview(mount, session) {
  let monday = mondayOf(new Date());

  async function draw() {
    const end = new Date(monday); end.setDate(monday.getDate() + 6);
    const lock = lockDeadline(monday);
    const lockText = `${lock.toLocaleDateString('en-GB', { weekday: 'short' })} ${pad2(lock.getDate())}/${pad2(lock.getMonth() + 1)}, 10:00`;
    const isThisWeek = isoDay(monday) === isoDay(mondayOf(new Date()));

    // v4 (p11): week bar · figures strip · one table, a row per person.
    // Remind and Approve submitted wait, disabled, until the week has loaded.
    mount.innerHTML = `
      <div class="wt-bar">
        <div class="wt-bar-l">
          <button type="button" class="btn wt-step" id="wk-prev" aria-label="Previous week">‹</button>
          <span class="wt-range">${escapeHtml(rangeLabel(monday))}</span>
          <button type="button" class="btn wt-step" id="wk-next" aria-label="Next week">›</button>
          <button type="button" class="linkbtn wt-now" id="wk-now"${isThisWeek ? ' hidden' : ''}>This week</button>
        </div>
        <div class="wt-bar-r">
          <span class="wt-lockline">Unfiled sheets lock ${escapeHtml(lockText)}</span>
          <button type="button" class="btn" id="rv-remind-all" disabled aria-disabled="true">Remind</button>
          <button type="button" class="btn primary" id="rv-approve-all" disabled aria-disabled="true">Approve submitted</button>
        </div>
      </div>
      <div id="rev-flash" class="wt-lockline" role="status" hidden></div>
      <div id="rev-figures"></div>
      <div id="rev-table">${skeleton({ band: false, rows: 6 })}</div>`;

    mount.querySelector('#wk-prev').addEventListener('click', () => { monday.setDate(monday.getDate() - 7); draw(); });
    mount.querySelector('#wk-next').addEventListener('click', () => { monday.setDate(monday.getDate() + 7); draw(); });
    mount.querySelector('#wk-now').addEventListener('click', () => { monday = mondayOf(new Date()); draw(); });

    let sheets, users, leave;
    try {
      [sheets, users, leave] = await Promise.all([timesheetsForWeek(isoDay(monday)), allUsers(), listLeaveRequests().catch(() => [])]);
    } catch (e) {
      mount.querySelector('#rev-table').innerHTML = `<div class="load-error"><div class="empty-title">The team’s week did not load</div><p>${escapeHtml(e.message || e)}</p></div>`;
      return;
    }
    const byEmail = Object.fromEntries(sheets.map((s) => [s.userEmail, s]));
    // A room screen (roles only 'Display', or users.roomScreen — the Meeting
    // Room account, people.js) is not a person and never files a timesheet —
    // leave it out, as meeting mode does (v4.1.1, v4.7.2).
    // Someone whose timesheets are "Not required" (Team, 26/10/2026) is listed
    // only for a week they DID file — never as missing, never reminded — and
    // isn't counted in "Sheets in N of M" (timesheet-required.js).
    const team = weekTeam({ users, byEmail });
    const active = team.people;

    // Day-by-day totals across the whole team for the week.
    const dayTotals = Object.fromEntries(DAY_KEYS.map((k) => [k, 0]));
    let grand = 0;
    for (const s of sheets) {
      for (const r of (s.rows || [])) {
        DAY_KEYS.forEach((k) => { const m = parse(r.days && r.days[k]); dayTotals[k] += m; grand += m; });
      }
    }
    // Weekend columns show only when the team actually worked them.
    const visKeys = DAY_KEYS.filter((k, i) => i < 5 || dayTotals[k] > 0);

    const sheetsIn = team.sheetsIn;
    const submitted = active.filter((u) => byEmail[u.email] && byEmail[u.email].status === 'submitted');
    const noSheet = team.noSheet;
    const weekendAsks = active.filter((u) => byEmail[u.email] && byEmail[u.email].weekendRequested && !byEmail[u.email].weekendApproved);
    const pastDeadline = new Date() >= lock;
    const firstName = (u) => titleCase(String(u.displayName || u.email || '').split(/\s+/)[0]);
    const names = (list) => (list.length <= 2 ? list.map(firstName).join(' and ') : `${list.length} people`);

    // Remind / Approve submitted carry their counts; each says why when it can't.
    const bulk = bulkState({ active, byEmail, roles: session.roles, mailbox: hasFeature('workspaceMailbox'),
      started: monday <= new Date(), pastDeadline });
    const setBtn = (b, st, enabled) => {
      b.textContent = st.label; b.title = st.title;
      b.disabled = !enabled; if (enabled) b.removeAttribute('aria-disabled'); else b.setAttribute('aria-disabled', 'true');
    };
    const remindBtn = mount.querySelector('#rv-remind-all');
    setBtn(remindBtn, bulk.remind, bulk.remind.enabled);
    const allBtn = mount.querySelector('#rv-approve-all');
    setBtn(allBtn, bulk.approve, bulk.approve.enabled);

    // "Mon to Thu" — the days the team-hours figure covers so far.
    const todayIdx = (new Date().getDay() + 6) % 7;
    const daysCovered = isThisWeek ? (todayIdx === 0 ? 'Monday so far' : `Mon to ${DAY_SHORT[Math.min(todayIdx, 6)]}`)
      : monday > new Date() ? 'nothing yet' : 'the whole week';
    const figs = [
      { label: 'Waiting on you', value: String(submitted.length), note: 'submitted, not yet approved', late: submitted.length > 0 },
      { label: 'Sheets in', value: `${sheetsIn} of ${team.required.length}`, note: noSheet.length ? `${names(noSheet)} missing` : 'everyone is in' },
      { label: 'Team hours', value: hm(grand), note: daysCovered },
      { label: 'Weekend requests', value: String(weekendAsks.length), note: weekendAsks.length ? names(weekendAsks) : 'none' },
    ];
    mount.querySelector('#rev-figures').innerHTML = `<div class="figs wt-figs">${figs.map((f) => `<div class="fig">
      <div class="fig-label">${escapeHtml(f.label)}</div>
      <div class="fig-value${f.late ? ' late' : ''}">${escapeHtml(f.value)}</div>
      <div class="fig-note">${escapeHtml(f.note)}</div></div>`).join('')}</div>`;

    // Approved leave inside the week, per person: "Annual leave from Thu".
    const weekIso = DAY_KEYS.map((k, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return isoDay(d); });
    const leaveLine = (u) => {
      const r = (leave || []).find((x) => x.status === 'approved' && String(x.userEmail || '').toLowerCase() === String(u.email || '').toLowerCase()
        && x.from <= weekIso[4] && (x.to || x.from) >= weekIso[0]);
      if (!r) return '';
      const i = Math.max(0, weekIso.indexOf(weekIso.find((d) => d >= r.from) || weekIso[0]));
      return `${leaveWord(r.kind, { short: true })} from ${DAY_SHORT[i]}`;
    };

    // Needs action first (submitted), then no sheet, then draft / sent back, then approved.
    const rank = (s) => (!s ? 1 : s.status === 'submitted' ? 0 : s.status === 'approved' ? 3 : 2);
    const people = active.slice().sort((a, b) => rank(byEmail[a.email]) - rank(byEmail[b.email])
      || String(a.displayName || '').localeCompare(String(b.displayName || '')));

    const rowHtml = (u) => {
      const s = byEmail[u.email];
      const perDay = Object.fromEntries(DAY_KEYS.map((k) => [k, 0]));
      for (const r of (s ? s.rows || [] : [])) DAY_KEYS.forEach((k) => { perDay[k] += parse(r.days && r.days[k]); });
      const mins = s ? sheetMinutes(s) : 0;
      const locked = s && (s.status === 'draft' || s.status === 'rejected') && !s.unlocked && pastDeadline;
      let sub = ''; let subLate = false;
      if (!s) { sub = `Nothing filed — locks ${lock.toLocaleDateString('en-GB', { weekday: 'short' })} 10:00`; subLate = true; }
      else if (s.unlockRequested && !s.unlocked) { sub = 'Asked to unlock the week'; subLate = true; }
      else if (s.weekendRequested && !s.weekendApproved) sub = 'Asked to open the weekend';
      else if (locked) { sub = 'Locked — past the deadline'; subLate = true; }
      else if (s.status === 'approved' && s.reviewedBy) sub = `Approved by ${firstName({ displayName: s.reviewedBy })}`;
      else if (s.status === 'rejected' && s.reviewedBy) sub = `Sent back by ${firstName({ displayName: s.reviewedBy })}`;
      else sub = leaveLine(u);
      const status = !s ? pill('No sheet', 'warn')
        : s.status === 'rejected' ? pill('Sent back', 'warn')
          : s.status === 'submitted' ? pill('Submitted', 'info') : pill(titleCase(s.status || 'draft'), STATUS_TONES[s.status]);
      const id = s ? escapeHtml(s.id) : '';
      const acts = [];
      if (s && s.weekendRequested && !s.weekendApproved) {
        acts.push(`<button type="button" class="linkbtn rv-wk-decline" data-id="${id}" data-confirm-label="Confirm decline">Decline</button>`);
        acts.push(`<button type="button" class="btn sm rv-wk-approve" data-id="${id}" data-confirm-label="Confirm open">Open weekend</button>`);
      }
      if (s && ((s.unlockRequested && !s.unlocked) || locked)) acts.push(`<button type="button" class="btn sm rv-unlock" data-id="${id}" data-confirm-label="Confirm unlock">Unlock</button>`);
      if (s && s.status === 'submitted') {
        if (!(s.weekendRequested && !s.weekendApproved)) acts.push(`<button type="button" class="btn sm rv-reject" data-id="${id}">Send back</button>`);
        acts.push(`<button type="button" class="btn sm primary rv-approve" data-id="${id}" data-confirm-label="Confirm approve">Approve</button>`);
      }
      if (!s) {
        acts.push(bulk.remind.rowEnabled
          ? `<button type="button" class="btn sm rv-remind" data-email="${escapeHtml(u.email)}" title="Email ${escapeHtml(firstName(u))} a reminder">Remind</button>`
          : `<button type="button" class="btn sm" disabled aria-disabled="true" title="${escapeHtml(bulk.remind.title)}">Remind</button>`);
      }
      return `<tr class="rv-row${s ? ' live' : ''}" data-email="${escapeHtml(u.email)}"${s ? ' tabindex="0" title="Open the detailed timesheet"' : ''}>
        <td><div class="rv-person"><span class="av tk4-av" aria-hidden="true">${escapeHtml(initials(u.displayName))}</span>
          <span class="rv-who"><b>${escapeHtml(titleCase(u.displayName))}</b>${sub ? `<span class="${subLate ? 'late' : ''}">${escapeHtml(sub)}</span>` : ''}</span></div></td>
        ${visKeys.map((k) => `<td class="c num rv-d${perDay[k] ? '' : ' dim'}">${perDay[k] ? escapeHtml(hm(perDay[k])) : '–'}</td>`).join('')}
        <td class="r num rv-week${s ? '' : ' dim'}">${s ? escapeHtml(hm(mins)) : '–'}</td>
        <td>${status}</td>
        <td class="r"><div class="rv-acts">${acts.join('')}</div></td>
      </tr>`;
    };

    mount.querySelector('#rev-table').innerHTML = `<div class="wt-scroll"><table class="wt-table rv-table">
      <thead><tr><th>Person</th>${visKeys.map((k) => `<th class="c rv-d">${escapeHtml(DAY_SHORT[DAY_KEYS.indexOf(k)])}</th>`).join('')}<th class="r">Week</th><th>Status</th><th class="r">Action</th></tr></thead>
      <tbody class="arrive">${people.map(rowHtml).join('') || `<tr><td colspan="${visKeys.length + 4}" class="dim">Nobody on the team yet.</td></tr>`}</tbody>
      <tfoot><tr><td class="lbl">Team total</td>${visKeys.map((k) => `<td class="c rv-d">${escapeHtml(hm(dayTotals[k]))}</td>`).join('')}
        <td class="r grand">${escapeHtml(hm(grand))}</td><td></td><td></td></tr></tfoot>
    </table></div>`;

    // ── Handlers. Every state change asks first: the first press arms the
    // button ("Confirm approve"), a second press within 5s writes. ─────────
    const twoPress = (sel, write) => mount.querySelectorAll(sel).forEach((b) => b.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      if (!b.dataset.armed) {
        b.dataset.armed = '1'; const was = b.textContent; b.textContent = b.dataset.confirmLabel || 'Confirm';
        setTimeout(() => { if (b.isConnected && b.dataset.armed && !b.disabled) { delete b.dataset.armed; b.textContent = was; } }, 5000);
        return;
      }
      b.disabled = true;
      await write(b.dataset.id).catch((e) => alert(e.message || e));
      draw();
    }));
    twoPress('.rv-approve', (id) => updateTimesheet(id, {
      status: 'approved', reviewedBy: session.displayName, reviewedAt: new Date().toISOString(), reviewNote: null,
    }));
    // Unlock a locked week (Karan 23/07) — either from a request or directly.
    twoPress('.rv-unlock', (id) => updateTimesheet(id, {
      unlocked: true, unlockedBy: session.displayName, unlockedAt: new Date().toISOString(), unlockRequested: false,
    }));
    // Weekend-working approvals (Karan 13/07): open Sat/Sun on this person's week.
    twoPress('.rv-wk-approve', (id) => updateTimesheet(id, {
      weekendApproved: true, weekendRequested: false, weekendApprovedBy: session.displayName,
    }));
    twoPress('.rv-wk-decline', (id) => updateTimesheet(id, { weekendRequested: false }));
    // One send-back flow only (A3): opens the review sheet with the send-back
    // section ready — a comment is required before anything is sent back.
    mount.querySelectorAll('.rv-reject').forEach((b) => b.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const row = b.closest('.rv-row');
      const u = active.find((x) => x.email === (row && row.dataset.email));
      if (u) openReviewDetail(u, byEmail[u.email], { sendBack: true });
    }));
    // ── Approve submitted (25/10/2026): confirm, then one by one ───────────
    // The write is the row's Approve exactly (updateTimesheet), plus one guard:
    // a sheet that is no longer submitted is left alone, not approved.
    const approveFields = () => ({ status: 'approved', reviewedBy: session.displayName, reviewedAt: new Date().toISOString(), reviewNote: null });
    allBtn.addEventListener('click', () => {
      if (allBtn.disabled) return;
      const items = bulk.submitted.map((u) => ({ u, s: byEmail[u.email], mins: sheetMinutes(byEmail[u.email]) }));
      const total = items.reduce((t, x) => t + x.mins, 0);
      const state = new Map(items.map((x) => [x.s.id, { st: 'ready', error: '' }]));
      let listEl = null;
      const paintList = () => {
        if (!listEl) return;
        listEl.innerHTML = `${items.map((x) => {
          const r = state.get(x.s.id);
          const tag = r.st === 'done' ? pill('Approved', 'ok') : r.st === 'failed' ? pill('Not approved', 'error') : r.st === 'working' ? '<span class="dim">Approving…</span>' : '';
          return `<div class="rv-bulk-row" style="display:flex;align-items:baseline;gap:12px;padding:8px 0;border-bottom:1px solid var(--hair-soft)">
            <span style="flex:1;min-width:0"><b>${escapeHtml(titleCase(x.u.displayName || x.u.email))}</b>${r.error ? `<span class="late" style="display:block;font-size:12px">${escapeHtml(r.error)}</span>` : ''}</span>
            ${tag}<span class="num">${escapeHtml(hm(x.mins))}</span></div>`;
        }).join('')}
          <div style="display:flex;gap:12px;padding:10px 0 0;font-weight:500"><span style="flex:1">Total</span><span class="num">${escapeHtml(hm(total))}</span></div>`;
      };
      const dlg = openDialog({
        title: approveButtonLabel(items.length),
        subtitle: `${rangeLabel(monday)} — each is approved as if you pressed Approve on its row.`,
        render: (body) => { listEl = document.createElement('div'); body.appendChild(listEl); paintList(); },
        primary: {
          label: approveButtonLabel(items.length),
          onSubmit: async (_v, api) => {
            const todo = items.filter((x) => state.get(x.s.id).st !== 'done');
            const res = await approveEach(todo, (x) => updateTimesheet(x.s.id, approveFields(), { expectStatus: 'submitted' }),
              ({ item, state: st, error }) => {
                state.set(item.s.id, { st, error: error || '' }); paintList();
                const n = items.filter((x) => state.get(x.s.id).st === 'done').length;
                api.setNote(`${n} of ${items.length} approved`);
              });
            draw();   // the list behind shows what went through, either way
            if (res.failed.length) {
              const btn = dlg.foot.querySelector('.tf-btn-primary');
              if (btn) { btn.dataset.verb = `Try ${res.failed.length === 1 ? 'that one' : `those ${res.failed.length}`} again`; btn.dataset.working = 'Approving…'; }
              const okN = items.length - res.failed.length;
              throw new Error(`${okN} approved. ${res.failed.length} ${res.failed.length === 1 ? 'was' : 'were'} not — see the list.`);
            }
            toastV4(items.length === 1 ? 'Approved 1 timesheet' : `Approved ${items.length} timesheets`);
          },
        },
      });
    });

    // ── Remind (25/10/2026): the server says who and what, then sends ──────
    const flash = mount.querySelector('#rev-flash');
    const openRemind = async (btn, emails) => {
      if (btn.disabled) return;
      const was = btn.textContent; btn.disabled = true; btn.textContent = 'Checking…';
      let chk;
      try { chk = await callFunction('remindTimesheets', { week: isoDay(monday), mode: 'check', ...(emails ? { emails } : {}) }); }
      catch (e) { toastV4(e.message || String(e), 5000); return; }
      finally { btn.disabled = false; btn.textContent = was; }
      const n = chk.recipients.length;
      const who = n === 1 ? chk.recipients[0].name : `${n} people`;
      const dlg = openDialog({
        title: n ? `Remind ${titleCase(who)}` : 'Nobody to remind',
        subtitle: `Their timesheet for ${chk.range} isn’t in. It locks ${chk.lockText}.`,
        render: (body) => {
          body.innerHTML = `
            ${chk.live ? '' : `<div class="rv-preview-note" style="background:var(--nav-hover);color:var(--anchor);border-radius:10px;padding:10px 14px;font-size:12.5px;margin-bottom:14px">
              <b>Preview mode:</b> the reminder will come to you (${escapeHtml(chk.previewTo || '')}), not to them, so you can check it. Nobody else is emailed until reminders are switched on.</div>`}
            ${n ? `<div style="font-size:12px;color:var(--ink-3);margin-bottom:6px">${chk.live ? 'To' : 'Would go to'}</div>
              <div style="display:flex;flex-direction:column;gap:4px;margin-bottom:14px">${chk.recipients.map((r) => `<span><b>${escapeHtml(titleCase(r.name))}</b> <span class="dim">${escapeHtml(r.email)}</span></span>`).join('')}</div>` : ''}
            ${chk.skipped.length && emails ? `<div style="font-size:12.5px;color:var(--ink-3);margin-bottom:14px">${chk.skipped.map((x) => `${escapeHtml(titleCase(x.name))}: ${escapeHtml(x.reason)}`).join('<br>')}</div>` : ''}
            ${n ? `<div style="font-size:12px;color:var(--ink-3);margin-bottom:6px">The email, from Track’s mailbox${n > 1 ? ' — each gets their own first name' : ''}</div>
              <div style="border:1px solid var(--hair);border-radius:10px;padding:12px 14px;font-size:13px;line-height:1.5">
                <div style="font-weight:500;margin-bottom:8px">${escapeHtml(chk.email.subject)}</div>
                <div style="white-space:pre-wrap">${escapeHtml(chk.email.body)}</div></div>` : '<p class="g-note">Everyone asked for has a timesheet in, has left, or can’t be emailed.</p>'}`;
        },
        primary: {
          label: chk.live ? `Send ${n === 1 ? 'the reminder' : `${n} reminders`}` : 'Send the preview to me',
          onSubmit: async () => {
            if (!n) return;
            const res = await callFunction('remindTimesheets', { week: isoDay(monday), mode: 'send', ...(emails ? { emails } : {}) });
            const failed = res.recipients.filter((r) => r.status === 'failed');
            const skipped = res.recipients.filter((r) => r.status === 'skipped');
            let msg;
            if (!res.live) msg = 'Preview mode: the reminder came to you, not to them.';
            else msg = `Reminded ${res.sent === 1 ? '1 person' : `${res.sent} people`}${skipped.length ? `; ${skipped.length} already reminded today` : ''}.`;
            flash.textContent = msg; flash.hidden = false;
            if (failed.length) throw new Error(`${msg} Not sent to ${failed.map((r) => titleCase(r.name)).join(', ')} — try again.`);
            toastV4(msg, 5000);
          },
        },
      });
      if (!n) dlg.api.setPrimary('notyet');
    };
    remindBtn.addEventListener('click', () => openRemind(remindBtn, null));
    mount.querySelectorAll('.rv-remind').forEach((b) => b.addEventListener('click', (ev) => {
      ev.stopPropagation();
      openRemind(b, [b.dataset.email]);
    }));
    // ── The review sheet (handoff design_handoff_track_process A1–A5) ──────
    // Header + figures + per-entry notes + queries + history + one Send-back
    // flow (required comment, quick reasons, row flags, re-opens a locked
    // week) + reviewer edit mode with an edits[] audit trail. Status VALUE
    // stays 'rejected' (repo wins); the LABEL everywhere is "Sent back".
    const MONEY_ROLES = ['Owner', 'Director', 'Finance', 'Coordinator'];
    const canEdit = (session.roles || []).some((r) => MONEY_ROLES.includes(r));
    const CHARGE_OUT = Number(orgSetting('chargeOutRate', null)) > 0 ? Number(orgSetting('chargeOutRate', null)) : 60;   // settings.chargeOutRate (was a hard-coded 60; Settings stage 3)
    // The person's expected week: their own, else the company's (ASP 37:30), else
    // none — and none for someone whose timesheets are not required (26/10/2026).
    const contractOf = (u) => expectedMinutes(u, orgSetting('weeklyContractMinutes', null));
    const CAPSL = 'font-size:var(--text-caps);font-weight:500;letter-spacing:var(--tracking-caps);text-transform:uppercase;color:var(--ink-3)';

    // Billable / Non-billable / Absence — r.billable wins; else derived: a real
    // project number bills, absence-words are absence, the rest is internal.
    const classify = (r) => {
      if (r.billable) return r.billable;
      if (r.internal) return 'Non-billable'; // project-free ASP time (Admin, Training, Meeting…)
      const label = `${r.number || ''} ${r.label || ''}`.toLowerCase();
      if (/leave|holiday|sick|absence|bank hol/.test(label)) return 'Absence';
      return /^\s*\d{4,5}\b/.test(String(r.number || '')) ? 'Billable' : 'Non-billable';
    };
    const DAYCOLS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];

    const openReviewDetail = async (u, s, { sendBack = false } = {}) => {
      const end2 = new Date(monday); end2.setDate(monday.getDate() + 6);
      const sub = `${monday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${end2.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
      const { content, close } = openOverlay({ title: titleCase(u.displayName), subtitle: sub, wide: true });

      // Last week's total for the same person — context the figure never had.
      const prevMon = new Date(monday); prevMon.setDate(prevMon.getDate() - 7);
      let lastWeekMin = null;
      try {
        const prev = (await timesheetsForWeek(isoDay(prevMon))).find((x) => x.userEmail === u.email);
        if (prev) lastWeekMin = sheetMinutes(prev);
      } catch (_) { /* context only */ }

      const state = { mode: sendBack ? 'sendback' : 'view', edits: [], flags: new Set(), queryFor: null };

      const paint = () => {
        if (!s) { content.innerHTML = '<div style="padding:26px"><p class="g-note">No timesheet submitted for this week.</p></div><div class="form-foot"><button class="btn ghost" id="rvd-cancel">Close</button></div>'; wire(); return; }
        const srows = (s.rows || []).filter((r) => Object.values(r.days || {}).some(parse));
        const wknd = srows.some((r) => parse(r.days && r.days.sat) || parse(r.days && r.days.sun));
        const cols = DAYCOLS.filter(([, ], i) => i < 5 || wknd);
        const grid = `minmax(170px,1.6fr) 92px repeat(${cols.length},56px) 62px`;
        const minW = 170 + 92 + cols.length * 56 + 62;
        const eff = (r, k) => { // effective minutes after this session's pending edits
          const e = state.edits.find((x) => x.rowKey === rowKey(r) && x.day === k);
          return e ? parse(e.to) : parse(r.days && r.days[k]);
        };
        const rowKey = (r) => `${r.number || ''}|${r.label || ''}`;
        const dtot = {}; cols.forEach(([k]) => { dtot[k] = 0; }); let gt = 0;
        const split = { Billable: 0, 'Non-billable': 0, Absence: 0 };
        srows.forEach((r) => {
          const cls = classify(r);
          cols.forEach(([k]) => { const m = eff(r, k); dtot[k] += m; gt += m; split[cls] = (split[cls] || 0) + m; });
        });
        const savedEdits = Array.isArray(s.edits) ? s.edits : [];
        const queries = srows.flatMap((r) => (Array.isArray(r.queries) ? r.queries.map((q) => ({ ...q, rowKey: rowKey(r), number: r.number })) : []));

        // Per-entry notes (A2): dayNotes wins; a legacy row.note migrates onto
        // the first day carrying time, display-side.
        const entryNotes = (r) => {
          const notes = { ...(r.dayNotes || {}) };
          if (r.note && !Object.keys(notes).length) {
            const first = cols.find(([k]) => parse(r.days && r.days[k]));
            if (first) notes[first[0]] = r.note;
          }
          return notes;
        };

        const bodyRows = srows.map((r) => {
          const rk = rowKey(r);
          const notes = entryNotes(r);
          const rQueries = queries.filter((q) => q.rowKey === rk);
          const rt = cols.reduce((x, [k]) => x + eff(r, k), 0);
          const cls = classify(r);
          const noteless = cols.filter(([k]) => eff(r, k) && !notes[k]).map(([k, l]) => `${l} ${hm(eff(r, k))}`);
          const cells = cols.map(([k]) => {
            const m = eff(r, k);
            const edited = state.edits.some((x) => x.rowKey === rk && x.day === k) || savedEdits.some((x) => x.rowKey === rk && x.day === k);
            const q = rQueries.some((x) => x.day === k && !x.reply);
            const underline = q ? 'box-shadow:inset 0 -2px 0 var(--brand-orange);' : (notes[k] ? 'box-shadow:inset 0 -2px 0 var(--hair);' : '');
            if (state.mode === 'edit') {
              return `<input class="rvd-cell" data-row="${escapeHtml(rk)}" data-day="${k}" value="${m ? hm(m) : ''}" style="width:52px;justify-self:center;text-align:center;border:1px solid ${edited ? 'var(--brand-blue)' : 'var(--hair)'};border-radius:6px;padding:6px 2px;font-family:var(--font-technical);font-size:12.5px;">`;
            }
            return `<span class="num" style="${underline}${m ? '' : 'color:var(--ink-3);'}padding-bottom:2px">${m ? hm(m) : '–'}${edited ? ' <span title="edited" style="color:var(--link)">·</span>' : ''}</span>`;
          }).join('');
          const noteLines = cols.filter(([k]) => notes[k]).map(([k, l]) => `
            <div style="display:flex;gap:10px;align-items:baseline;font-size:12.5px;color:var(--ink-2);padding:3px 0 0 12px">
              <span style="font-family:var(--font-technical);font-size:11px;color:var(--ink-3);white-space:nowrap">${l} ${hm(parse(r.days && r.days[k]) || 0)}</span>
              <span style="min-width:0">${escapeHtml(notes[k])}</span>
              <button class="rvd-query" data-row="${escapeHtml(rk)}" data-day="${k}" style="background:none;border:0;color:var(--link);font-size:11.5px;cursor:pointer;padding:0;white-space:nowrap">Query</button>
            </div>`).join('');
          const queryLines = rQueries.map((q) => `
            <div style="display:flex;gap:10px;align-items:baseline;font-size:12.5px;padding:3px 0 0 12px">
              <span style="font-family:var(--font-technical);font-size:11px;color:var(--brand-orange);white-space:nowrap">${escapeHtml((DAYCOLS.find(([k]) => k === q.day) || ['', q.day])[1])} query</span>
              <span style="color:var(--ink);min-width:0">${escapeHtml(q.q)} <span style="color:var(--ink-3)">— ${escapeHtml(q.by || '')}${q.reply ? ` · replied: ${escapeHtml(q.reply)}` : ' · awaiting reply'}</span></span>
            </div>`).join('');
          return `<div style="border-bottom:1px solid var(--hair-soft);padding:6px 0">
            <div class="ct-row" style="grid-template-columns:${grid};cursor:default;border-bottom:0">
              <span class="strong" style="display:flex;align-items:center;gap:8px">${state.mode === 'sendback' ? `<input type="checkbox" class="rvd-flag" data-row="${escapeHtml(rk)}"${state.flags.has(rk) ? ' checked' : ''} style="width:16px;height:16px">` : ''}${escapeHtml(r.number || '—')}${r.label ? ` <span class="sec" style="font-weight:400">${escapeHtml(r.label)}</span>` : ''}</span>
              <span style="font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:${cls === 'Billable' ? 'var(--brand-blue)' : cls === 'Absence' ? 'var(--brand-orange)' : 'var(--ink-3)'};align-self:center">${cls}</span>
              ${cells}
              <span class="num strong">${hm(rt)}</span>
            </div>
            ${noteLines}${queryLines}
            ${noteless.length && (noteLines || queryLines) ? `<div style="font-size:11px;color:var(--ink-3);padding:3px 0 0 12px">${escapeHtml(noteless.join(' · '))} — no note</div>` : ''}
          </div>`;
        }).join('');

        const CONTRACT_MIN = contractOf(u);
        const over = CONTRACT_MIN ? gt - CONTRACT_MIN : 0;
        const meta = s.status === 'rejected' ? 'Sent back' : titleCase(s.status);
        const submitted = s.submittedAt ? new Date(s.submittedAt) : null;
        const role = (u.roles || [])[0] || '';
        const history = [
          ...(submitted ? [{ at: s.submittedAt, t: `Submitted ${submitted.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${submitted.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` }] : []),
          ...savedEdits.map((e) => ({ at: e.at, t: `${e.day ? (DAYCOLS.find(([k]) => k === e.day) || ['', e.day])[1] : ''} on ${e.rowKey.split('|')[0]} changed ${hm(parse(e.from))} → ${hm(parse(e.to))} by ${e.by} — ${e.reason}` })),
          ...queries.map((q) => ({ at: q.at, t: `Query on ${q.number || ''}: ${q.q} — ${q.by}` })),
          ...(s.reviewedAt ? [{ at: s.reviewedAt, t: `${meta} by ${s.reviewedBy || ''}${s.reviewNote ? ` — “${s.reviewNote}”` : ''}` }] : []),
        ].sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));

        const lock = lockDeadline(monday);
        const fig = (label, val, sub2 = '') => `<div style="display:flex;flex-direction:column;gap:4px"><span style="${CAPSL}">${label}</span><span style="font-size:var(--figure-sm);font-weight:500;letter-spacing:-0.03em;line-height:1;font-family:var(--font-technical)">${val}</span>${sub2 ? `<span style="font-size:11.5px;color:var(--ink-3)">${sub2}</span>` : ''}</div>`;

        content.innerHTML = `
          <div style="padding:20px 26px;display:flex;flex-direction:column;gap:18px">
            ${state.mode === 'edit' ? '<div style="background:var(--ink-fill);color:var(--ink-fill-ink);border-radius:10px;padding:10px 14px;font-size:12.5px">Editing as a reviewer — every change is recorded against your name, and the submitted figures stay in the history.</div>' : ''}
            <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap">
              <span style="width:34px;height:34px;border-radius:50%;background:var(--brief-bg);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:500">${escapeHtml(initials(u.displayName))}</span>
              <div style="display:flex;flex-direction:column">
                <span style="font-size:14px;font-weight:500">${escapeHtml(titleCase(u.displayName))}${role ? ` <span style="font-weight:400;color:var(--ink-3);font-size:12px">· ${escapeHtml(role)}</span>` : ''}</span>
                <span style="font-size:11.5px;color:var(--ink-3)">${submitted ? `submitted ${submitted.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ${submitted.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'not submitted'}</span>
              </div>
              <div style="flex:1"></div>
              ${pill(meta, STATUS_TONES[s.status])}
            </div>
            <div style="display:flex;align-items:flex-end;gap:28px;flex-wrap:wrap">
              <div style="display:flex;flex-direction:column;gap:4px">
                <span style="font-size:var(--anchor-md);font-weight:500;letter-spacing:-0.03em;line-height:0.9;color:var(--anchor)">${hm(gt)}</span>
                <span style="font-size:11.5px;color:${over > 0 ? 'var(--brand-orange)' : 'var(--ink-3)'}">${!CONTRACT_MIN ? 'logged this week' : over === 0 ? `on the ${hm(CONTRACT_MIN)} contract week` : `${over > 0 ? '+' : '−'}${hm(Math.abs(over))} vs the ${hm(CONTRACT_MIN)} contract week`}</span>
              </div>
              ${fig('Billable', hm(split.Billable || 0), `≈ £${Math.round(((split.Billable || 0) / 60) * CHARGE_OUT).toLocaleString('en-GB')} at £${CHARGE_OUT}`)}
              ${fig('Non-billable', hm(split['Non-billable'] || 0))}
              ${fig('Absence', hm(split.Absence || 0))}
              ${fig('Last week', lastWeekMin == null ? '—' : hm(lastWeekMin))}
            </div>
            <div class="card-table"><div class="scroll"><div style="min-width:${minW}px">
              <div class="ct-head" style="grid-template-columns:${grid}">
                <div>Project</div><div>Class</div>${cols.map(([, l]) => `<div class="num">${l}</div>`).join('')}<div class="num">Total</div>
              </div>
              ${bodyRows || '<div class="ct-empty">No time logged this week.</div>'}
              <div class="ct-group" style="grid-template-columns:${grid}">
                <span class="ct-group-label" style="text-transform:uppercase;letter-spacing:var(--tracking-caps);font-weight:var(--weight-medium)">Total</span><span></span>
                ${cols.map(([k]) => `<span class="num">${dtot[k] ? hm(dtot[k]) : '–'}</span>`).join('')}
                <span class="num strong">${hm(gt)}</span></div>
            </div></div></div>
            ${history.length ? `<div style="display:flex;flex-direction:column;gap:6px"><span style="${CAPSL}">History</span>${history.map((h) => `<span style="font-size:12px;color:var(--ink-2)">${escapeHtml(h.t)}</span>`).join('')}</div>` : ''}
            <div class="form-error" id="rvd-err"></div>
            ${state.mode === 'sendback' ? `<div style="display:flex;flex-direction:column;gap:10px;border-top:1px solid var(--hair);padding-top:14px">
              <span style="${CAPSL};color:var(--brand-orange)">Send back — the person sees this on their sheet</span>
              <div style="display:flex;gap:8px;flex-wrap:wrap">${['Hours look wrong', 'Missing a note', 'Wrong project', 'Weekend needs a reason'].map((c) => `<button class="rvd-chip" style="border:1px solid var(--hair);border-radius:999px;padding:7px 13px;font-size:12px;background:none;cursor:pointer">${c}</button>`).join('')}</div>
              <textarea class="f-textarea" id="rvd-note" rows="3" placeholder="What needs fixing? (required)"></textarea>
              <span style="font-size:11.5px;color:var(--ink-3)">Tick the rows in question above — the person is told which lines to look at. Sending back re-opens the week for them, even past the Tuesday lock.</span>
            </div>` : ''}
            ${state.mode === 'edit' ? `<div style="display:flex;flex-direction:column;gap:10px;border-top:1px solid var(--hair);padding-top:14px">
              <div id="rvd-changes" style="font-size:12px;color:var(--ink-2)"></div>
              <textarea class="f-textarea" id="rvd-reason" rows="2" placeholder="Why are you changing this sheet? (required)"></textarea>
            </div>` : ''}
          </div>
          <div class="form-foot" style="display:flex;align-items:center;gap:10px">
            <span style="font-size:11.5px;color:var(--ink-3);margin-right:auto">Locks ${lock.toLocaleDateString('en-GB', { weekday: 'short' })} ${lock.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
            <button class="btn ghost" id="rvd-cancel">Close</button>
            ${state.mode === 'view' && canEdit ? '<button class="btn ghost" id="rvd-edit">Edit sheet</button>' : ''}
            ${state.mode === 'view' && s.status === 'submitted' ? '<button class="btn ghost danger" id="rvd-sendback">Send back…</button>' : ''}
            ${state.mode === 'view' && s.status === 'submitted' ? `<button class="btn primary" id="rvd-approve">Approve ${hm(gt)}</button>` : ''}
            ${state.mode === 'sendback' ? '<button class="btn primary" id="rvd-sendback-go" disabled>Send back</button>' : ''}
            ${state.mode === 'edit' ? `<button class="btn primary" id="rvd-save-edits" disabled>Save changes — ${hm(gt)}</button>` : ''}
          </div>`;
        wire();
      };

      const wire = () => {
        content.querySelector('#rvd-cancel')?.addEventListener('click', close);
        content.querySelector('#rvd-approve')?.addEventListener('click', async (ev) => {
          ev.target.disabled = true;
          try { await updateTimesheet(s.id, { status: 'approved', reviewedBy: session.displayName, reviewedAt: new Date().toISOString(), reviewNote: null }); close(); draw(); }
          catch (e) { ev.target.disabled = false; content.querySelector('#rvd-err').textContent = e.message || e; }
        });
        content.querySelector('#rvd-sendback')?.addEventListener('click', () => { state.mode = 'sendback'; paint(); });
        content.querySelector('#rvd-edit')?.addEventListener('click', () => { state.mode = 'edit'; paint(); });
        // send-back: comment REQUIRED; chips append; flags collected; re-opens the lock
        const sbNote = content.querySelector('#rvd-note');
        const sbGo = content.querySelector('#rvd-sendback-go');
        if (sbNote && sbGo) {
          sbNote.addEventListener('input', () => { sbGo.disabled = !sbNote.value.trim(); });
          content.querySelectorAll('.rvd-chip').forEach((c) => c.addEventListener('click', () => {
            sbNote.value = (sbNote.value ? sbNote.value.replace(/\s*$/, '') + ' — ' : '') + c.textContent + ': ';
            sbNote.focus(); sbGo.disabled = !sbNote.value.trim();
          }));
          content.querySelectorAll('.rvd-flag').forEach((f) => f.addEventListener('change', () => {
            if (f.checked) state.flags.add(f.dataset.row); else state.flags.delete(f.dataset.row);
          }));
          sbGo.addEventListener('click', async () => {
            sbGo.disabled = true;
            try {
              await updateTimesheet(s.id, {
                status: 'rejected', reviewedBy: session.displayName, reviewedAt: new Date().toISOString(),
                reviewNote: sbNote.value.trim(), reviewFlags: [...state.flags],
                unlocked: true, unlockedBy: session.displayName, unlockedAt: new Date().toISOString(),
              });
              close(); draw();
            } catch (e) { sbGo.disabled = false; content.querySelector('#rvd-err').textContent = e.message || e; }
          });
        }
        // edit mode: cells become inputs; a reason is required; edits[] appended
        const saveBtn = content.querySelector('#rvd-save-edits');
        if (saveBtn) {
          const reason = content.querySelector('#rvd-reason');
          const changes = content.querySelector('#rvd-changes');
          const refresh = () => {
            changes.innerHTML = state.edits.map((e) => `${escapeHtml(e.rowKey.split('|')[0])} ${escapeHtml((DAYCOLS.find(([k]) => k === e.day) || ['', e.day])[1])} changed from ${hm(parse(e.from))} to ${hm(parse(e.to))}`).join('<br>') || 'No changes yet.';
            saveBtn.disabled = !(state.edits.length && reason.value.trim());
          };
          reason.addEventListener('input', refresh);
          content.querySelectorAll('.rvd-cell').forEach((inp) => inp.addEventListener('change', () => {
            const rk = inp.dataset.row, day = inp.dataset.day;
            const row = (s.rows || []).find((r) => `${r.number || ''}|${r.label || ''}` === rk);
            const from = row && row.days ? (row.days[day] || '') : '';
            state.edits = state.edits.filter((e) => !(e.rowKey === rk && e.day === day));
            if (String(inp.value).trim() !== String(from).trim()) state.edits.push({ rowKey: rk, day, from, to: inp.value.trim() });
            inp.style.borderColor = state.edits.some((e) => e.rowKey === rk && e.day === day) ? 'var(--brand-blue)' : 'var(--hair)';
            refresh();
          }));
          refresh();
          saveBtn.addEventListener('click', async () => {
            saveBtn.disabled = true;
            try {
              // Rows are updated so every total in the app is correct; the
              // ORIGINALS live on in edits[] — nothing is silently overwritten.
              const stamp = new Date().toISOString();
              const audit = state.edits.map((e) => ({ ...e, by: session.displayName, at: stamp, reason: reason.value.trim() }));
              // The edits are applied to the rows AS THEY ARE NOW, inside the
              // transaction. Mapping over the copy this screen loaded would
              // write back an array with no hours logged since — silently.
              s.rows = await editTimesheetRows(s.id, (rows) => rows.map((r) => {
                const rk = `${r.number || ''}|${r.label || ''}`;
                const mine = state.edits.filter((e) => e.rowKey === rk);
                if (!mine.length) return r;
                const days = { ...(r.days || {}) };
                mine.forEach((e) => { days[e.day] = e.to; });
                return { ...r, days };
              }), { edits: [...(Array.isArray(s.edits) ? s.edits : []), ...audit] });
              close(); draw();
            } catch (e) { saveBtn.disabled = false; content.querySelector('#rvd-err').textContent = e.message || e; }
          });
        }
        // query an entry (A2): a one-line question recorded on the row
        content.querySelectorAll('.rvd-query').forEach((b) => b.addEventListener('click', async () => {
          const q = window.prompt('Query this entry — the person sees your question:');
          if (!q || !q.trim()) return;
          const rk = b.dataset.row, day = b.dataset.day;
          try {
            s.rows = await editTimesheetRows(s.id, (rows) => rows.map((r) => (`${r.number || ''}|${r.label || ''}` === rk
              ? { ...r, queries: [...(Array.isArray(r.queries) ? r.queries : []), { day, q: q.trim(), by: session.displayName, at: new Date().toISOString() }] }
              : r)));
            paint();
          }
          catch (e) { content.querySelector('#rvd-err').textContent = e.message || e; }
        }));
      };
      paint();
    };
    mount.querySelectorAll('.rv-row.live').forEach((row) => {
      const open = () => { const u = active.find((x) => x.email === row.dataset.email); if (u) openReviewDetail(u, byEmail[row.dataset.email]); };
      row.addEventListener('click', (ev) => { if (ev.target.closest('button')) return; open(); });
      row.addEventListener('keydown', (ev) => { if (ev.target !== row) return; if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); } });
    });
  }

  await draw();
}
